import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Newspaper,
  Users,
  BookOpen,
  MapPin,
  Radar,
  BarChart3,
  TrendingUp,
  Sparkles,
  Headset,
  Clock,
  Wrench,
  ClipboardCheck,
  Settings2,
  Smartphone,
  CalendarDays,
  FileText,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Reveal } from "@/components/Shared";
import { MOCKS, MODULE_COLORS, MODULE_SECONDARY, ModuleVisual } from "@/components/ModuleMocks";

const SUB_ICONS = {
  intranet: [Newspaper, Users, BookOpen],
  operations: [MapPin, Radar, BarChart3],
  kpi: [BarChart3, TrendingUp, Sparkles],
  support: [Headset, Clock, CheckCircle2],
  metier: [Wrench, ClipboardCheck, Settings2],
  magasin: [Smartphone, CalendarDays, FileText],
};

const ModulePage = () => {
  const { moduleId } = useParams();
  const { t, lang } = useLanguage();
  const mod = t.featuresPage.modules.find((m) => m.id === moduleId);
  const detail = t.modulePages && t.modulePages[moduleId];
  const hasModule = !!MOCKS[moduleId];
  const colors = MODULE_COLORS[moduleId];
  usePageTitle(mod ? mod.titleMain : undefined);

  if (!mod || !detail || !hasModule) {
    return (
      <main data-testid="module-not-found" className="pt-[76px]">
        <div className="mx-auto max-w-3xl px-5 py-32 text-center">
          <p className="text-slate-500">404</p>
          <Link
            to="/fonctionnalites"
            className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-blue-600"
          >
            <ArrowLeft className="h-4 w-4" /> {t.modulePages?.common?.backLink || "Retour"}
          </Link>
        </div>
      </main>
    );
  }

  const nextIndex = (t.featuresPage.modules.findIndex((m) => m.id === moduleId) + 1) % t.featuresPage.modules.length;
  const nextMod = t.featuresPage.modules[nextIndex];
  const common = t.modulePages.common;
  const secondaryShots = MODULE_SECONDARY[moduleId] || [];

  return (
    <main data-testid={`module-page-${moduleId}`} className="pt-[76px]">
      <section
        className={`relative overflow-hidden bg-gradient-to-b ${colors.bg} via-white to-white py-16 lg:py-24`}
      >
        <div className="pointer-events-none absolute inset-0 bg-grid-light opacity-60 [mask-image:radial-gradient(ellipse_at_top,black_20%,transparent_70%)]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <div>
            <Link
              to="/fonctionnalites"
              data-testid="module-back-link"
              className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 transition-colors hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4" /> {common.backLink}
            </Link>
            <div className="mt-8 flex items-center gap-3">
              <span className="font-mono-orbit text-sm font-bold text-emerald-500">{mod.chapter}</span>
              <span className="h-px w-8 bg-emerald-500/50" />
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-slate-500">{mod.titleMain}</span>
            </div>
            <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl text-balance">
              {mod.titleMain}
              <span className={`block ${colors.text}`}>{mod.titleSub}</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">{detail.intro}</p>
            <ul className="mt-7 grid gap-x-6 gap-y-3 sm:grid-cols-2">
              {mod.points.map((point) => (
                <li key={point} className="flex items-start gap-2.5 text-sm font-medium text-slate-700">
                  <CheckCircle2 className={`mt-0.5 h-5 w-5 shrink-0 ${colors.check}`} />
                  {point}
                </li>
              ))}
            </ul>
            <Link
              to="/demo"
              data-testid="module-demo-button"
              className="group mt-9 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-600/25 transition-all duration-300 hover:bg-blue-700"
            >
              {common.demoButton}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            data-testid="module-hero-mock"
            className={moduleId === "magasin" ? "flex justify-center" : ""}
          >
            <ModuleVisual id={moduleId} />
          </motion.div>
        </div>
      </section>

      {secondaryShots.length > 0 && (
        <section className="bg-slate-50/70 py-16 lg:py-24" data-testid="module-screenshots">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal>
              <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                {lang === "fr" ? "Le produit en vrai" : "The product, for real"}
              </h2>
            </Reveal>
            <div className={`mt-10 grid gap-6 ${secondaryShots.length > 1 ? "lg:grid-cols-2" : ""}`}>
              {secondaryShots.map((src, i) => (
                <Reveal key={src} delay={i * 0.1}>
                  <div
                    data-testid={`module-screenshot-${i}`}
                    className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_24px_48px_-20px_rgba(10,15,29,0.2)]"
                  >
                    <img src={src} alt={`Interface Orbit — ${mod.titleMain}`} className="block w-full" loading="lazy" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 lg:py-28" data-testid="module-subfeatures">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
              {common.subFeaturesTitle}
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {detail.subFeatures.map((feature, i) => {
              const Icon = SUB_ICONS[moduleId][i];
              return (
                <Reveal key={feature.title} delay={i * 0.1}>
                  <div
                    data-testid={`module-subfeature-${i}`}
                    className="group h-full rounded-2xl border border-slate-100 bg-white p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-slate-200 hover:shadow-[0_24px_48px_-16px_rgba(10,15,29,0.12)]"
                  >
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-500 group-hover:scale-110 ${colors.tile}`}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{feature.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{feature.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#0A0F1D] py-16 lg:py-24" data-testid="module-benefits">
        <div className="pointer-events-none absolute inset-0 bg-grid-navy" />
        <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <h2 className="max-w-xl font-display text-2xl font-bold tracking-tight text-white sm:text-3xl text-balance">
              {common.benefitsTitle}
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-10 md:grid-cols-3">
            {detail.benefits.map((benefit, i) => (
              <Reveal key={benefit.title} delay={i * 0.1}>
                <div data-testid={`module-benefit-${i}`}>
                  <span className="font-mono-orbit text-sm font-bold text-emerald-400">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-3 font-display text-lg font-bold text-white">{benefit.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{benefit.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Link
              to={`/fonctionnalites/${nextMod.id}`}
              data-testid="module-next-link"
              className="group flex flex-col items-start justify-between gap-6 rounded-2xl border border-slate-100 bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_48px_-16px_rgba(10,15,29,0.12)] sm:flex-row sm:items-center lg:p-10"
            >
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-slate-400">{common.nextLabel}</p>
                <p className="mt-2 font-display text-xl font-bold text-slate-900 lg:text-2xl">
                  <span className="font-mono-orbit mr-3 text-sm font-bold text-emerald-500">{nextMod.chapter}</span>
                  {nextMod.titleMain} — <span className={MODULE_COLORS[nextMod.id].text}>{nextMod.titleSub}</span>
                </p>
              </div>
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white transition-transform duration-300 group-hover:translate-x-1.5">
                <ArrowRight className="h-5 w-5" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default ModulePage;
